import React from 'react';
import { Shield, Users, Mail, ExternalLink } from 'lucide-react';

const LegalConsentCheckboxes = ({ 
  onConsentChange, 
  showDataTransfer = true, 
  showMarketing = true, 
  required = ['terms'], 
  className = "",
  variant = "default" // "default" | "compact" | "inline"
}) => {
  const [consents, setConsents] = React.useState({
    terms: false,           // תנאי שימוש + פרטיות - חובה
    dataTransfer: false,    // העברת מידע לצד ג' - חובה לליד
    marketing: false        // דיוור שיווקי - אופציונלי
  });

  const handleConsentChange = (consentType, value) => {
    const newConsents = {
      ...consents,
      [consentType]: value
    };
    setConsents(newConsents);
    
    // העברת הנתונים להורה
    if (onConsentChange) {
      onConsentChange(newConsents);
    }
  };

  const isConsentRequired = (consentType) => required.includes(consentType);
  
  // וריאציה קומפקטית למודל - 3 תיבות נפרדות
  if (variant === "compact") {
    return (
      <div className={`space-y-3 ${className}`}>
        {/* 1. תנאי שימוש ומדיניות פרטיות - חובה */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms-consent-compact"
            checked={consents.terms}
            onChange={(e) => handleConsentChange('terms', e.target.checked)}
            className="mt-1 w-4 h-4 accent-green-500 flex-shrink-0"
            required={isConsentRequired('terms')}
          />
          <div className="flex-grow">
            <label htmlFor="terms-consent-compact" className="text-white/90 text-xs leading-relaxed block cursor-pointer">
              קראתי ואני מסכים/ה ל
              <a href="/terms" target="_blank" className="text-blue-300 hover:text-blue-200 underline mx-1">
                תנאי השימוש
              </a>
              ול
              <a href="/privacy" target="_blank" className="text-blue-300 hover:text-blue-200 underline mx-1">
                מדיניות הפרטיות
              </a>
              של האתר, לרבות איסוף ושימוש במידע אישי כמפורט בהם
              {isConsentRequired('terms') && <span className="text-red-300 mr-1">*</span>}
            </label>
          </div>
        </div>

        {/* 2. העברת מידע לצד שלישי - חובה */}
        {showDataTransfer && (
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="data-transfer-compact"
              checked={consents.dataTransfer}
              onChange={(e) => handleConsentChange('dataTransfer', e.target.checked)}
              className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
              required={isConsentRequired('dataTransfer')}
            />
            <div className="flex-grow">
              <label htmlFor="data-transfer-compact" className="text-white/90 text-xs leading-relaxed block cursor-pointer">
                אני מסכים/ה שהפרטים שמסרתי יועברו לצדדים שלישיים לצורך קבלת הצעות או שירותים מטעמם
                {isConsentRequired('dataTransfer') && <span className="text-red-300 mr-1">*</span>}
              </label>
            </div>
          </div>
        )}

        {/* 3. דיוור שיווקי - אופציונלי */}
        {showMarketing && (
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="marketing-consent-compact"
              checked={consents.marketing}
              onChange={(e) => handleConsentChange('marketing', e.target.checked)}
              className="mt-1 w-4 h-4 accent-blue-500 flex-shrink-0"
            />
            <div className="flex-grow">
              <label htmlFor="marketing-consent-compact" className="text-white/70 text-xs leading-relaxed block cursor-pointer">
                אני מסכים/ה לקבל מכם דיוורים שיווקיים (דוא"ל / SMS). ידוע לי כי אוכל להסיר את עצמי בכל עת
                <span className="text-white/50 mr-1">(אופציונלי)</span>
              </label>
            </div>
          </div>
        )}

        {/* הודעת אזהרה למקרה שלא סומנו שדות חובה */}
        {((isConsentRequired('terms') && !consents.terms) || 
          (isConsentRequired('dataTransfer') && !consents.dataTransfer)) && (
          <div className="mt-2 p-2 bg-red-500/20 border border-red-400/30 rounded-lg">
            <p className="text-red-300 text-xs flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">!</span>
              יש לסמן את כל השדות החובה (*) כדי להמשיך
            </p>
          </div>
        )}
      </div>
    );
  }

  // וריאציה inline קצרה
  if (variant === "inline") {
    return (
      <div className={`flex items-start gap-2 ${className}`}>
        <input
          type="checkbox"
          id="terms-inline"
          checked={consents.terms}
          onChange={(e) => handleConsentChange('terms', e.target.checked)}
          className="mt-1 w-4 h-4 accent-blue-500 flex-shrink-0"
          required={isConsentRequired('terms')}
        />
        <label htmlFor="terms-inline" className="text-sm text-gray-700 leading-relaxed">
          אני מסכים/ה ל
          <a href="/terms" target="_blank" className="text-blue-600 hover:text-blue-800 mx-1 underline">
            תנאי השימוש
          </a>
          ול
          <a href="/privacy" target="_blank" className="text-blue-600 hover:text-blue-800 mx-1 underline">
            מדיניות הפרטיות
          </a>
          {isConsentRequired('terms') && <span className="text-red-500 mr-1">*</span>}
        </label>
      </div>
    );
  }

  // וריאציה מלאה (ברירת מחדל) - לטופס יצירת קשר - 3 תיבות נפרדות
  return (
    <div className={`space-y-4 ${className}`}>
      {/* 1. תנאי שימוש ופרטיות - חובה */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-grow">
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="terms-consent"
              checked={consents.terms}
              onChange={(e) => handleConsentChange('terms', e.target.checked)}
              className="mt-1 w-4 h-4 accent-blue-600 flex-shrink-0"
              required={isConsentRequired('terms')}
            />
            <div className="flex-grow">
              <label htmlFor="terms-consent" className="text-sm text-gray-800 leading-relaxed cursor-pointer">
                <strong>קראתי ואני מסכים/ה לתנאי השימוש ולמדיניות הפרטיות של האתר</strong>
                {isConsentRequired('terms') && <span className="text-red-500 mr-1">*</span>}
                <br />
                <span className="text-xs text-gray-600">
                  לרבות איסוף ושימוש במידע אישי כמפורט בהם.
                </span>
              </label>
              <div className="flex gap-3 mt-2">
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  תנאי שימוש <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  מדיניות פרטיות <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. העברת מידע לצד שלישי - חובה לליד */}
      {showDataTransfer && (
        <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <Users className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-grow">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="data-transfer-consent"
                checked={consents.dataTransfer}
                onChange={(e) => handleConsentChange('dataTransfer', e.target.checked)}
                className="mt-1 w-4 h-4 accent-orange-600 flex-shrink-0"
                required={isConsentRequired('dataTransfer')}
              />
              <label htmlFor="data-transfer-consent" className="text-sm text-gray-800 leading-relaxed cursor-pointer">
                <strong>אני מסכים/ה שהפרטים שמסרתי יועברו לצדדים שלישיים</strong>
                {isConsentRequired('dataTransfer') && <span className="text-red-500 mr-1">*</span>}
                <br />
                <span className="text-xs text-gray-600">
                  לצורך קבלת הצעות או שירותים מטעמם בתחום הפנסיוני והפיננסי.
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* 3. דיוור שיווקי - אופציונלי */}
      {showMarketing && (
        <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
          <Mail className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-grow">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="marketing-consent"
                checked={consents.marketing}
                onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                className="mt-1 w-4 h-4 accent-green-600 flex-shrink-0"
              />
              <label htmlFor="marketing-consent" className="text-sm text-gray-800 leading-relaxed cursor-pointer">
                <strong>אני מסכים/ה לקבל מכם דיוורים שיווקיים</strong>
                <span className="text-gray-500 text-xs mr-1">(אופציונלי)</span>
                <br />
                <span className="text-xs text-gray-600">
                  דוא"ל / SMS. ידוע לי כי אוכל להסיר את עצמי בכל עת.
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* הודעת אזהרה אם לא סומנו שדות חובה */}
      {((isConsentRequired('terms') && !consents.terms) || 
        (isConsentRequired('dataTransfer') && !consents.dataTransfer)) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-700 flex items-center gap-2">
            <span className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">!</span>
            יש לסמן את כל השדות החובה (*) כדי להמשיך
          </p>
        </div>
      )}
    </div>
  );
};

// פונקציה עוזרת לבדיקה אם כל ההסכמות הנדרשות נתנו
export const validateConsents = (consents, required = ['terms']) => {
  return required.every(consentType => consents[consentType] === true);
};

// פונקציה עוזרת להכנת אובייקט ההסכמות לשליחה
export const formatConsentsForSubmission = (consents) => {
  return {
    termsAccepted: consents.terms,
    dataTransferAccepted: consents.dataTransfer,
    marketingAccepted: consents.marketing,
    consentTimestamp: new Date().toISOString(),
    consentVersion: '1.0'
  };
};

export default LegalConsentCheckboxes;